// src/pages/LeagueNav.js
import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useGuardedNavigate } from "../utils/navGuard";
import { useAuth } from "../context/AuthContext";
import { PrimaryButton, SecondaryButton } from "../central/Button";
import styled from "styled-components";
import { db } from "../lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import theme from "../central/theme";
import { logEvent } from "../utils/EventLogger";

// Module-level cache — persists for the lifetime of the browser session.
// Avoids a Firestore read every time the user navigates between pages (which
// remounts LeagueNav because each page renders its own <Layout>).
const leagueDocCache = new Map();

const StyledPrimaryButton = styled(PrimaryButton)`
  padding: 0.3rem 0.5rem;
  &:hover {
    background-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.textOnAccent || "black"};
  }
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  padding: 0.2rem 0.5rem;
  &:hover {
    background-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.textOnAccent || "black"};
  }
`;

const PageTitle = styled.h2`
  text-align: center;
  color: black;
  font-family: ${theme.typography.fontFamily.body};
  margin-top: 0.3rem;
  margin-bottom: 0.3rem;
  font-size: 1.2rem;

  @media (max-width: 767px) {
    display: none;
  }
`;

const GradientDivider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(
    to right,
    rgba(0,0,0,0),
    rgba(0,0,0,0.15),
    rgba(0,0,0,0)
  );
  margin: 0.2rem 0;

  @media (max-width: 767px) {
    display: none;
  }
`;

const LeagueNavContainer = styled.div`
  display: flex;
  gap: 0.3rem;
  justify-content: ${props => (props.$overflow ? "flex-start" : "center")};
  flex-wrap: nowrap;
  overflow-x: auto;
  white-space: nowrap;
  max-width: 100%;
  text-align: center;
  margin-top: 0.2rem;
  margin-bottom: 0.2rem;
  font-family: ${theme.typography.fontFamily.body};
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }

  @media (max-width: 767px) {
    display: none;
  }

  @media (max-width: 600px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    white-space: nowrap;
    padding: 0.rem 0;
    box-sizing: border-box;
    gap: 0.4rem;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE 10+ */
    scroll-behavior: auto;
    &::-webkit-scrollbar {
      display: none; /* Safari and Chrome */
    }
    &::before,
    &::after {
      content: "";
      display: block;
      min-width: 0.75rem;
      flex-shrink: 0;
    }
  }
`;

export default function LeagueNav({ leagueID: propLeagueID }) {
  const { leagueID: paramLeagueID } = useParams();
  const leagueID = propLeagueID || paramLeagueID;
  const { currentUser } = useAuth();
  const navigate = useGuardedNavigate();
  const location = useLocation();
  const userName = currentUser?.displayName || "";
  const roleForLeague = (leagueID && currentUser?.roles?.[leagueID]) ? currentUser.roles[leagueID] : "";

  const [league, setLeague] = useState(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!leagueID) return;

    // Serve from cache if already fetched this session
    if (leagueDocCache.has(leagueID)) {
      setLeague(leagueDocCache.get(leagueID));
      return;
    }

    const fetchLeague = async () => {
      try {
        const leagueSnap = await getDoc(doc(db, "leagues", leagueID));
        if (leagueSnap.exists()) {
          const data = { id: leagueSnap.id, ...leagueSnap.data() };
          leagueDocCache.set(leagueID, data);
          setLeague(data);
        } else {
          console.warn("League not found:", leagueID);
        }
      } catch (error) {
        console.error("Error fetching league:", error);
      }
    };

    fetchLeague();
  }, [leagueID]);

  useEffect(() => {
    requestAnimationFrame(() => {
      const container = document.querySelector(".league-nav-container");
      if (container) container.scrollLeft = 0;
    });
  }, [location.pathname]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Check on mount and whenever the league or location changes (nav items may differ)
    const check = () =>
      setIsOverflowing(el.scrollWidth > el.clientWidth);

    check();

    // Re-check if the window is resized
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [leagueID, location.pathname]);

  if (!leagueID) {
    return null;
  }

  // Navigation handlers (instrumented)
  const handleNavigate = (path, target) => () => {
    // Avoid logging + navigating if already on the same path
    if (location.pathname === path) return;

    // Fire analytics without awaiting — never block navigation
    logEvent({
      eventType: "nav_click",
      leagueID: leagueID || "",
      userID: currentUser?.uid || "",
      userName,
      role: roleForLeague,
      source: "league_nav",
      metadata: {
        target: target || path,
      },
    }).catch((e) => console.warn("[analytics] nav_click failed", e));

    navigate(path);
  };

  // Determine page title based on current path
  let pageTitle = "";
  if (location.pathname.includes("/admin")) {
    pageTitle = "Manage League";
  } else if (location.pathname.includes("/league")) {
    pageTitle = "League";
  } else if(location.pathname.includes("/pickteam")) {
    pageTitle = "Pick Team";
  } else if (location.pathname.includes("/yourpoints")) {
    pageTitle = "Your Points";
  } else if (location.pathname.includes("/team")) {
    pageTitle = "Squad";
  } else if (location.pathname.includes("/leaderboard")) {
    pageTitle = "League Leaderboard";
  } else if (location.pathname.includes("/playerprices")) {
    pageTitle = "Player Prices";
  } else if (location.pathname.includes("/statistics")) {
    pageTitle = "Player Statistics";
  } else if (location.pathname.includes("/matches")) {
    pageTitle = "Match Results";
  } else if (location.pathname.includes("/admin")) {
    pageTitle = "Manage";
  }

  return (
    <>
      <LeagueNavContainer ref={containerRef} className="league-nav-container" $overflow={isOverflowing}>
        {league &&
          currentUser &&
          (league.createdBy === currentUser.uid ||
            league.admins?.includes(currentUser.uid)) && (
          <StyledSecondaryButton active={location.pathname.includes("/admin")} onClick={handleNavigate(`/admin/${leagueID}`, "admin")}>
            Manage
          </StyledSecondaryButton>
        )}
        <StyledPrimaryButton active={location.pathname.includes("/league")} onClick={handleNavigate(`/league/${leagueID}`, "league")}>League</StyledPrimaryButton>
        <StyledPrimaryButton active={location.pathname.includes("/pickteam")} onClick={handleNavigate(`/pickteam/${leagueID}`, "pickteam")}>Pick Team</StyledPrimaryButton>
        <StyledPrimaryButton active={location.pathname.includes("/yourpoints")} onClick={handleNavigate(`/yourpoints/${leagueID}`, "yourpoints")}>Points</StyledPrimaryButton>
        <StyledPrimaryButton active={location.pathname.includes("/team")} onClick={handleNavigate(`/team/${leagueID}`, "team")}>Squad</StyledPrimaryButton>
        <StyledPrimaryButton active={location.pathname.includes("/leaderboard")} onClick={handleNavigate(`/leaderboard/${leagueID}`, "leaderboard")}>Leaderboard</StyledPrimaryButton>
        <StyledPrimaryButton active={location.pathname.includes("/playerprices")} onClick={handleNavigate(`/playerprices/${leagueID}`, "playerprices")}>Prices</StyledPrimaryButton>
        <StyledPrimaryButton active={location.pathname.includes("/statistics")} onClick={handleNavigate(`/statistics/${leagueID}`, "statistics")}>Statistics</StyledPrimaryButton>
        <StyledPrimaryButton active={location.pathname.includes("/matches")} onClick={handleNavigate(`/matches/${leagueID}/GW1`, "matches")}>Matches</StyledPrimaryButton>
      </LeagueNavContainer>
      {pageTitle && location.pathname.includes(leagueID) && (
        <>
          <GradientDivider />
          <PageTitle>{pageTitle}</PageTitle>
          <GradientDivider />
        </>
      )}
    </>
  );
}